/**
 * Asset Library body — left nav (Templates / Library Assets groups), a
 * header with a close button, and a shared footer with a per-tab action.
 *
 * Only "Slide Templates" and "Brand Templates" have real panels; every other
 * tab renders the shared "coming soon" placeholder. Neither real tab
 * inserts/applies anything itself: Slide Templates hands off to
 * PowerPoint.run() via the task pane, and Brand Templates only routes into
 * Apply Brand's Saved Templates tab with a pick pre-selected. That routing
 * runs the other way too — Apply Brand's own Saved Templates tab can open
 * THIS component straight onto a template's detail view (see
 * brandTemplatePreviewHandoff.ts), which is why the default tab is decided
 * at mount rather than hardcoded.
 *
 * The footer is shared chrome, but its label/button are entirely per-tab
 * (see types.ts's AssetLibraryFooterState) — Slide Templates reports
 * selection count + "Insert into deck" via onSelectionStateChange + an
 * imperative ref; Brand Templates reports its own label via
 * onFooterStateChange. Every other tab falls back to
 * DEFAULT_ASSET_LIBRARY_FOOTER_STATE.
 *
 * No outer scrim/overlay of its own — mounted either inline behind a
 * `<dialog>` backdrop (no Office Dialog API) or filling the Office Dialog
 * window's own card (AssetLibraryDialogApp.tsx).
 */

import { useCallback, useMemo, useRef, useState, type RefObject } from "react";
import {
  SlideTemplatesPanel,
  type SlideTemplatesPanelHandle,
  type SlideTemplatesSelectionState,
} from "../../slide-templates/components/SlideTemplatesPanel";
import type { UseSlideTemplatesOptions } from "../../slide-templates/hooks/useSlideTemplates";
import { logSlideTemplatesError } from "../../slide-templates/services/slideTemplatesApi";
import {
  PresentationTemplatesPanel,
  type PresentationTemplatesPanelHandle,
  type PresentationTemplatesSelectionState,
} from "../../presentation-templates/components/PresentationTemplatesPanel";
import type { UsePresentationTemplatesOptions } from "../../presentation-templates/hooks/usePresentationTemplates";
import { logPresentationTemplatesError } from "../../presentation-templates/services/presentationTemplatesApi";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import { BrandTemplatesTab, type BrandTemplatesTabHandle } from "./BrandTemplatesTab";
import type { DialogBrandTemplates } from "../hooks/useDialogBrandTemplates";
import {
  AssetLibrarySelectionGuardContext,
  DEFAULT_ASSET_LIBRARY_FOOTER_STATE,
  type AssetLibraryFooterState,
  type AssetLibraryTabId,
  type RequestNewSelection,
} from "../types";
import styles from "./AssetLibraryDialog.module.css";

type LibraryTab = AssetLibraryTabId;

/** Tabs whose picks are held until inserted, so a pick elsewhere puts them at risk (BRD 8.10). */
type SelectionTab = "slide-templates" | "brand-templates" | "presentation-templates";

const SELECTION_TABS: readonly SelectionTab[] = ["slide-templates", "brand-templates", "presentation-templates"];

/** How a selection tab names itself and its picks in the "Start a new selection?" warning. */
const SELECTION_TAB_COPY: Readonly<Record<SelectionTab, { label: string; noun: string }>> = {
  "slide-templates": { label: "Slide Templates", noun: "template" },
  "brand-templates": { label: "Brand Templates", noun: "layout" },
  "presentation-templates": { label: "Presentation Templates", noun: "slide" },
};

interface TabSpec {
  id: LibraryTab;
  label: string;
  /** Muted tag after the label for a tab that isn't built yet. */
  badge?: string;
}

const TEMPLATE_TABS: TabSpec[] = [
  { id: "brand-templates", label: "Brand Templates" },
  { id: "slide-templates", label: "Slide Templates" },
  { id: "presentation-templates", label: "Presentation Templates" },
];

const LIBRARY_ASSET_TABS: TabSpec[] = [
  { id: "icons", label: "Icons" },
  { id: "logos", label: "Logos / Brands", badge: "(COMING SOON)" },
];

interface AssetLibraryContentProps {
  readonly onClose: () => void;
  /**
   * Tab to land on. Defaults to Brand Templates, the Asset Library's landing
   * tab; the pane's "Open Asset Library" notice button passes the tab the user
   * was last working in.
   */
  readonly initialTab?: AssetLibraryTabId;
  /**
   * Forwarded to SlideTemplatesPanel. Omitted for the in-pane mount (which
   * fetches and inserts directly); AssetLibraryDialogApp supplies the
   * hand-off/messageParent pair, since an Office Dialog has neither a bearer
   * token nor PowerPoint.run().
   */
  readonly slideTemplatesOptions?: UseSlideTemplatesOptions;
  /**
   * Forwarded to BrandTemplatesTab. Omitted for the in-pane mount (which
   * fetches directly); AssetLibraryDialogApp supplies its useDialogBrandTemplates()
   * result, since an Office Dialog has no bearer token for the org fetch.
   */
  readonly brandTemplates?: DialogBrandTemplates;
  /**
   * Forwarded to PresentationTemplatesPanel. Omitted for the in-pane mount
   * (which fetches and inserts directly); AssetLibraryDialogApp supplies the
   * hand-off/messageParent pair, since an Office Dialog has neither a bearer
   * token nor PowerPoint.run().
   */
  readonly presentationTemplatesOptions?: UsePresentationTemplatesOptions;
}

/** Footer copy for Slide Templates (BRD 8.7). */
const SLIDE_TEMPLATES_FOOTER_HINT = "Choose one or more slide templates to add after your current slide.";
const SLIDE_TEMPLATES_FOOTER_NOTE = "they'll go after your current slide";
const FOOTER_ERROR = "Couldn't reach the asset library";
const SLIDE_TEMPLATES_FOOTER_EMPTY = "0 slide templates";

/** Footer copy for Presentation Templates (BRD 8.8). */
const PRESENTATION_TEMPLATES_FOOTER_HINT = "Choose one or more slides to add after your current slide.";
const PRESENTATION_TEMPLATES_FOOTER_NOTE = "they'll go after your current slide";
const PRESENTATION_TEMPLATES_FOOTER_EMPTY = "0 presentation templates";

const DEFAULT_SLIDE_TEMPLATES_SELECTION: SlideTemplatesSelectionState = {
  count: 0,
  canInsert: false,
  isInserting: false,
};

const DEFAULT_PRESENTATION_TEMPLATES_SELECTION: PresentationTemplatesSelectionState = {
  count: 0,
  canInsert: false,
  isInserting: false,
};

export function AssetLibraryContent({
  onClose,
  initialTab = "brand-templates",
  slideTemplatesOptions,
  brandTemplates,
  presentationTemplatesOptions,
}: Readonly<AssetLibraryContentProps>) {
  // Brand Templates is the landing tab (BRD 7 / 8.1); Slide Templates is
  // reached explicitly from the Templates group. A staged preview (Apply
  // Brand's Saved Templates eye icon — see brandTemplatePreviewHandoff.ts) lands
  // here too, so no special-casing is needed.
  const [activeTab, setActiveTab] = useState<LibraryTab>(initialTab);
  // The Slide and Presentation panels hold the user's uninserted picks in their
  // own state, so once opened they stay mounted (just hidden) while the user
  // browses elsewhere — that is what makes a selection survive a tab change
  // (BRD 8.9). A tab nobody has opened yet isn't mounted, so nothing loads early.
  const [openedTabs, setOpenedTabs] = useState<ReadonlySet<LibraryTab>>(() => new Set([initialTab]));
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [slideTemplatesSelection, setSlideTemplatesSelection] = useState<SlideTemplatesSelectionState>(
    DEFAULT_SLIDE_TEMPLATES_SELECTION,
  );
  const slideTemplatesRef = useRef<SlideTemplatesPanelHandle>(null);
  const [brandFooterState, setBrandFooterState] = useState<AssetLibraryFooterState | null>(null);
  const [brandSelectionCount, setBrandSelectionCount] = useState(0);
  const brandTemplatesRef = useRef<BrandTemplatesTabHandle>(null);
  const [presentationTemplatesSelection, setPresentationTemplatesSelection] = useState<PresentationTemplatesSelectionState>(
    DEFAULT_PRESENTATION_TEMPLATES_SELECTION,
  );
  const presentationTemplatesRef = useRef<PresentationTemplatesPanelHandle>(null);

  const selectTab = useCallback((tab: LibraryTab) => {
    setActiveTab(tab);
    setOpenedTabs((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
    // Walking away from the warning without choosing cancels the new pick;
    // the selection it was about to clear is untouched.
    setPendingConfirm(null);
  }, []);

  const slidePickCount = slideTemplatesSelection.count;
  const presentationPickCount = presentationTemplatesSelection.count;
  const brandPickCount = brandSelectionCount;
  const selectionCounts = useMemo<Readonly<Record<SelectionTab, number>>>(
    () => ({
      "slide-templates": slidePickCount,
      "brand-templates": brandPickCount,
      "presentation-templates": presentationPickCount,
    }),
    [slidePickCount, brandPickCount, presentationPickCount],
  );

  /**
   * Gate for adding a pick in `from`: if ANOTHER selection tab still holds
   * uninserted picks, ask first instead of silently orphaning them (BRD 8.9).
   * Memoised, since it is the context value every panel re-reads.
   */
  const requestNewSelection = useCallback<RequestNewSelection>(
    (from, select, options) => {
      const other = SELECTION_TABS.find(
        (tab) => (tab !== from || options?.conflictsWithOwnPicks === true) && selectionCounts[tab] > 0,
      );
      if (other) {
        setPendingConfirm({ otherTab: other, select });
        return;
      }
      select();
    },
    [selectionCounts],
  );

  // Back to where the picks live, original selection intact.
  const keepPicks = (confirm: PendingConfirm) => {
    selectTab(confirm.otherTab);
    if (confirm.otherTab === "presentation-templates") presentationTemplatesRef.current?.revealSelection();
    else if (confirm.otherTab === "brand-templates") brandTemplatesRef.current?.showSelection();
  };

  // Discard the other tab's picks and carry on with the pick that triggered the warning.
  const clearAndContinue = (confirm: PendingConfirm) => {
    if (confirm.otherTab === "slide-templates") slideTemplatesRef.current?.clearSelection();
    else if (confirm.otherTab === "brand-templates") brandTemplatesRef.current?.clearSelection();
    else presentationTemplatesRef.current?.clearSelection();
    setPendingConfirm(null);
    confirm.select();
  };

  const isSlideTemplatesTab = activeTab === "slide-templates";
  const isBrandTemplatesTab = activeTab === "brand-templates";
  const isPresentationTemplatesTab = activeTab === "presentation-templates";

  const handleClearSlideTemplates = useCallback(() => {
    slideTemplatesRef.current?.clearSelection();
  }, []);

  const handleInsert = useCallback(() => {
    // Failures also surface inside SlideTemplatesPanel's own insertError
    // state — this catch only exists so the rejection is traced, not swallowed.
    slideTemplatesRef.current?.insertSelected().catch((error: unknown) => {
      logSlideTemplatesError("asset-library-insert-selected-failed", getErrorMessage(error, "Insert into deck failed"));
    });
  }, []);

  const handleClearPresentationTemplates = useCallback(() => {
    presentationTemplatesRef.current?.clearSelection();
  }, []);

  const handlePresentationInsert = useCallback(() => {
    // Failures also surface inside PresentationTemplatesPanel's own
    // insertError state — this catch only exists so the rejection is traced,
    // not swallowed.
    presentationTemplatesRef.current?.insertSelected().catch((error: unknown) => {
      logPresentationTemplatesError(
        "asset-library-presentation-insert-selected-failed",
        getErrorMessage(error, "Insert into deck failed"),
      );
    });
  }, []);

  let footerState: AssetLibraryFooterState;
  if (isSlideTemplatesTab) {
    footerState = slideTemplatesFooterState(slideTemplatesSelection, handleInsert, handleClearSlideTemplates);
  } else if (isBrandTemplatesTab && brandFooterState) {
    footerState = brandFooterState;
  } else if (isPresentationTemplatesTab) {
    footerState = presentationTemplatesFooterState(
      presentationTemplatesSelection,
      handlePresentationInsert,
      handleClearPresentationTemplates,
    );
  } else {
    footerState = DEFAULT_ASSET_LIBRARY_FOOTER_STATE;
  }

  return (
    <AssetLibrarySelectionGuardContext.Provider value={requestNewSelection}>
        <div className={styles.header}>
          <span className={styles.headerTitle}>Asset Library</span>
          <button type="button" className={styles.closeBtn} aria-label="Close Asset Library" onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.body}>
          <nav className={styles.nav} aria-label="Asset Library sections">
            <NavGroup title="TEMPLATES" tabs={TEMPLATE_TABS} activeTab={activeTab} onSelect={selectTab} />
            <NavGroup title="LIBRARY ASSETS" tabs={LIBRARY_ASSET_TABS} activeTab={activeTab} onSelect={selectTab} />
          </nav>

          <div className={styles.content}>
            {pendingConfirm && (
              <NewSelectionConfirm
                otherTab={pendingConfirm.otherTab}
                count={selectionCounts[pendingConfirm.otherTab]}
                label={
                  pendingConfirm.otherTab === "presentation-templates"
                    ? presentationTemplatesSelection.sourceName ?? undefined
                    : undefined
                }
                onKeep={() => keepPicks(pendingConfirm)}
                onClearAndContinue={() => clearAndContinue(pendingConfirm)}
              />
            )}
            <div className={styles.contentMain}>
            <ActiveTabPanel
              openedTabs={openedTabs}
              isSlideTemplatesTab={isSlideTemplatesTab}
              isBrandTemplatesTab={isBrandTemplatesTab}
              isPresentationTemplatesTab={isPresentationTemplatesTab}
              activeTab={activeTab}
              onClose={onClose}
              slideTemplatesRef={slideTemplatesRef}
              slideTemplatesOptions={slideTemplatesOptions}
              onSlideTemplatesSelectionChange={setSlideTemplatesSelection}
              brandTemplates={brandTemplates}
              brandTemplatesRef={brandTemplatesRef}
              onBrandFooterStateChange={setBrandFooterState}
              onBrandSelectionCountChange={setBrandSelectionCount}
              presentationTemplatesRef={presentationTemplatesRef}
              presentationTemplatesOptions={presentationTemplatesOptions}
              onPresentationTemplatesSelectionChange={setPresentationTemplatesSelection}
            />
            </div>

          <div className={styles.footer}>
            <div className={styles.selectionArea}>
              <span className={footerState.onClear ? `${styles.selectionCount} ${styles.selectionCountActive}` : styles.selectionCount}>
                {footerState.selectionLabel}
                {footerState.selectionNote && <span className={styles.selectionNote}> · {footerState.selectionNote}</span>}
              </span>
              {footerState.onClear && (
                <button type="button" className={styles.clearAllLink} onClick={footerState.onClear}>
                  Clear All
                </button>
              )}
            </div>
            <div className={styles.footerActions}>
              <button type="button" className={styles.cancelBtn} onClick={onClose}>
                Cancel
              </button>
              {!footerState.hideAction && (
                <button
                  type="button"
                  className={styles.insertBtn}
                  disabled={footerState.actionDisabled}
                  onClick={footerState.onAction}
                  title={footerState.actionLabel}
                >
                  {footerState.actionLabel}
                </button>
              )}
            </div>
          </div>
          </div>
        </div>
    </AssetLibrarySelectionGuardContext.Provider>
  );
}

interface PendingConfirm {
  /** The tab whose uninserted picks the new selection would clear. */
  readonly otherTab: SelectionTab;
  /** Performs the pick that triggered the warning, once confirmed. */
  readonly select: () => void;
}

/** The Slide Templates footer: guidance, then count + Clear All, or a state line. */
function slideTemplatesFooterState(
  selection: SlideTemplatesSelectionState,
  onInsert: () => void,
  onClear: () => void,
): AssetLibraryFooterState {
  if (selection.view === "error") {
    return { ...DEFAULT_ASSET_LIBRARY_FOOTER_STATE, selectionLabel: FOOTER_ERROR, hideAction: true };
  }
  if (selection.view === "empty") {
    return { ...DEFAULT_ASSET_LIBRARY_FOOTER_STATE, selectionLabel: SLIDE_TEMPLATES_FOOTER_EMPTY, hideAction: true };
  }
  const hasSelection = selection.count > 0;
  return {
    selectionLabel: hasSelection ? `${selection.count} selected` : SLIDE_TEMPLATES_FOOTER_HINT,
    selectionNote: hasSelection ? SLIDE_TEMPLATES_FOOTER_NOTE : undefined,
    onClear: hasSelection ? onClear : undefined,
    actionLabel: selection.isInserting ? "Inserting…" : "Insert into Deck",
    actionDisabled: !selection.canInsert,
    onAction: onInsert,
  };
}

/** The Presentation Templates footer (BRD 8.8): guidance, then count + Clear All. */
function presentationTemplatesFooterState(
  selection: PresentationTemplatesSelectionState,
  onInsert: () => void,
  onClear: () => void,
): AssetLibraryFooterState {
  if (selection.view === "error") {
    return { ...DEFAULT_ASSET_LIBRARY_FOOTER_STATE, selectionLabel: FOOTER_ERROR, hideAction: true };
  }
  if (selection.view === "empty") {
    return { ...DEFAULT_ASSET_LIBRARY_FOOTER_STATE, selectionLabel: PRESENTATION_TEMPLATES_FOOTER_EMPTY, hideAction: true };
  }
  const hasSelection = selection.count > 0;
  return {
    selectionLabel: hasSelection ? `${selection.count} selected` : PRESENTATION_TEMPLATES_FOOTER_HINT,
    selectionNote: hasSelection ? PRESENTATION_TEMPLATES_FOOTER_NOTE : undefined,
    onClear: hasSelection ? onClear : undefined,
    actionLabel: selection.isInserting ? "Inserting…" : "Insert into Deck",
    actionDisabled: !selection.canInsert,
    onAction: onInsert,
  };
}

/**
 * "Start a new selection?" — shown when the user starts a pick in one tab
 * while another still holds picks that were never inserted (BRD 8.9).
 */
function NewSelectionConfirm({
  otherTab,
  count,
  label: sourceLabel,
  onKeep,
  onClearAndContinue,
}: Readonly<{
  otherTab: SelectionTab;
  count: number;
  /** Names where the picks are, when more specific than the tab (a presentation's name). */
  label?: string;
  onKeep: () => void;
  onClearAndContinue: () => void;
}>) {
  const { label: tabLabel, noun } = SELECTION_TAB_COPY[otherTab];
  const label = sourceLabel ?? tabLabel;
  const single = count === 1;
  return (
    <div
      className={styles.confirm}
      role="alertdialog"
      aria-labelledby="new-selection-title"
      aria-describedby="new-selection-body"
    >
      <div id="new-selection-title" className={styles.confirmTitle}>
        Start a new selection?
      </div>
      <div id="new-selection-body" className={styles.confirmBody}>
        {`You've picked ${count} ${noun}${single ? "" : "s"} in ${label} that ${single ? "hasn't" : "haven't"} been inserted yet. Starting a new selection here will clear ${single ? "it" : "them"}.`}
      </div>
      <div className={styles.confirmActions}>
        <button type="button" className={styles.confirmSecondary} onClick={onKeep}>
          Keep My Picks
        </button>
        <button type="button" className={styles.confirmPrimary} onClick={onClearAndContinue} autoFocus>
          Clear and Continue
        </button>
      </div>
    </div>
  );
}

interface ActiveTabPanelProps {
  openedTabs: ReadonlySet<LibraryTab>;
  isSlideTemplatesTab: boolean;
  isBrandTemplatesTab: boolean;
  isPresentationTemplatesTab: boolean;
  activeTab: LibraryTab;
  onClose: () => void;
  slideTemplatesRef: RefObject<SlideTemplatesPanelHandle>;
  slideTemplatesOptions: UseSlideTemplatesOptions | undefined;
  onSlideTemplatesSelectionChange: (state: SlideTemplatesSelectionState) => void;
  brandTemplates: DialogBrandTemplates | undefined;
  brandTemplatesRef: RefObject<BrandTemplatesTabHandle>;
  onBrandFooterStateChange: (state: AssetLibraryFooterState | null) => void;
  onBrandSelectionCountChange: (count: number) => void;
  presentationTemplatesRef: RefObject<PresentationTemplatesPanelHandle>;
  presentationTemplatesOptions: UsePresentationTemplatesOptions | undefined;
  onPresentationTemplatesSelectionChange: (state: PresentationTemplatesSelectionState) => void;
}

/** Only "Slide Templates", "Brand Templates", and "Presentation Templates"
 *  have real panels — every other tab falls back to the shared "coming soon"
 *  placeholder. */
function ActiveTabPanel({
  openedTabs,
  isSlideTemplatesTab, isBrandTemplatesTab, isPresentationTemplatesTab, activeTab, onClose, slideTemplatesRef,
  slideTemplatesOptions, onSlideTemplatesSelectionChange, brandTemplates, brandTemplatesRef, onBrandFooterStateChange,
  onBrandSelectionCountChange, presentationTemplatesRef, presentationTemplatesOptions, onPresentationTemplatesSelectionChange,
}: Readonly<ActiveTabPanelProps>) {
  // The Slide Templates panel stays mounted once opened (hidden while another
  // tab is showing) so its uninserted picks survive browsing away.
  // The Presentation Templates panel does the same, for the same reason.
  const showSlideTemplates = openedTabs.has("slide-templates");
  const showBrandTemplates = openedTabs.has("brand-templates");
  const showPresentationTemplates = openedTabs.has("presentation-templates");
  const isOtherTab = !isSlideTemplatesTab && !isPresentationTemplatesTab;

  return (
    <>
      {showSlideTemplates && (
        <div className={styles.tabPane} hidden={!isSlideTemplatesTab}>
          <SlideTemplatesPanel
            ref={slideTemplatesRef}
            onSelectionStateChange={onSlideTemplatesSelectionChange}
            templatesOptions={slideTemplatesOptions}
          />
        </div>
      )}
      {showPresentationTemplates && (
        <div className={styles.tabPane} hidden={!isPresentationTemplatesTab}>
          <PresentationTemplatesPanel
            ref={presentationTemplatesRef}
            onSelectionStateChange={onPresentationTemplatesSelectionChange}
            templatesOptions={presentationTemplatesOptions}
          />
        </div>
      )}
      {showBrandTemplates && (
        <div className={styles.tabPane} hidden={!isBrandTemplatesTab}>
          <BrandTemplatesTab
            ref={brandTemplatesRef}
            onClose={onClose}
            dialogTemplates={brandTemplates}
            onFooterStateChange={onBrandFooterStateChange}
            onSelectionCountChange={onBrandSelectionCountChange}
          />
        </div>
      )}
      {isOtherTab && !isBrandTemplatesTab && (
        <ComingSoon tab={activeTab as Exclude<LibraryTab, "presentation-templates">} />
      )}
    </>
  );
}

function NavGroup({
  title,
  tabs,
  activeTab,
  onSelect,
}: Readonly<{
  title: string;
  tabs: TabSpec[];
  activeTab: LibraryTab;
  onSelect: (tab: LibraryTab) => void;
}>) {
  return (
    <div className={styles.navGroup}>
      <div className={styles.navGroupLabel}>{title}</div>
      <ul className={styles.navList}>
        {tabs.map((tab) => (
          <li key={tab.id}>
            <button
              type="button"
              className={`${styles.navItem} ${activeTab === tab.id ? styles.navItemActive : ""}`}
              aria-pressed={activeTab === tab.id}
              onClick={() => onSelect(tab.id)}
            >
              <NavIcon tab={tab.id} />
              <span className={styles.navLabel}>{tab.label}</span>
              {tab.badge && (
                <span className={styles.navBadge} aria-hidden="true">
                  {tab.badge}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The small outline icon beside each nav item — decorative, the label names the tab. */
function NavIcon({ tab }: Readonly<{ tab: LibraryTab }>) {
  return (
    <svg
      className={styles.navIcon}
      aria-hidden="true"
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {NAV_ICON_PATHS[tab]}
    </svg>
  );
}

const NAV_ICON_PATHS: Readonly<Record<LibraryTab, JSX.Element>> = {
  "brand-templates": (
    <>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <path d="M2 7h12" strokeWidth="2.2" />
      <path d="M4.5 10.5h3" />
    </>
  ),
  "slide-templates": (
    <>
      <rect x="2" y="2" width="9" height="7" rx="1" />
      <path d="M5 11.5h8a1 1 0 0 0 1-1V5" />
    </>
  ),
  "presentation-templates": (
    <>
      <rect x="2" y="2.5" width="12" height="8" rx="1" />
      <path d="M5 13.5h6M8 10.5v3M4.5 5.5h4M4.5 7.5h6" />
    </>
  ),
  icons: (
    <>
      <circle cx="5" cy="5" r="2.5" />
      <path d="M2 14l3-5 3 5z" />
      <rect x="9" y="8.5" width="5" height="5" rx="1" />
    </>
  ),
  logos: <path d="M8 2l5 1.8v3.9c0 3-2.1 5.2-5 6.3-2.9-1.1-5-3.3-5-6.3V3.8z" />,
};

const COMING_SOON_COPY: Record<Exclude<LibraryTab, "presentation-templates">, string> = {
  "brand-templates": "Brand templates are coming soon.",
  "slide-templates": "Slide templates are coming soon.",
  icons: "The icon library is coming soon.",
  logos: "Logos & brand assets are coming soon.",
};

function ComingSoon({ tab }: Readonly<{ tab: Exclude<LibraryTab, "presentation-templates"> }>) {
  return (
    <div className={styles.comingSoon}>
      <span className={styles.comingSoonIcon} aria-hidden="true">🗂</span>
      <span className={styles.comingSoonText}>{COMING_SOON_COPY[tab]}</span>
    </div>
  );
}
